import Modal from '@components/Modal'
import { backgroundOrange } from '@/theme/colors'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import {
	ButtonGroup,
	CancelButton,
	DateInput,
	DateInputGroup,
	DateLabel,
	ErrorMessage,
	ModalContent,
	SaveButton,
	UnsavedChangesText,
} from './EditTripDatesModal.styles'

interface EditTripDatesModalProps {
	isOpen: boolean
	onClose: () => void
	onSave: (startDate: string, endDate: string) => void
	initialStartDate: string
	initialEndDate: string
	isUpdating?: boolean
}

const EditTripDatesModal: React.FC<EditTripDatesModalProps> = ({
	isOpen,
	onClose,
	onSave,
	initialStartDate,
	initialEndDate,
	isUpdating = false,
}) => {
	const [startDate, setStartDate] = useState<Dayjs | null>(
		initialStartDate ? dayjs(initialStartDate) : null,
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate ? dayjs(initialEndDate) : null)
	const [error, setError] = useState('')

	// Reset dates when modal opens
	useEffect(() => {
		if (isOpen) {
			setStartDate(initialStartDate ? dayjs(initialStartDate) : null)
			setEndDate(initialEndDate ? dayjs(initialEndDate) : null)
			setError('')
		}
	}, [isOpen, initialStartDate, initialEndDate])

	const handleSave = () => {
		if (!startDate || !endDate) {
			setError('Please select both start and end dates')
			return
		}

		if (startDate.isAfter(endDate) || startDate.isSame(endDate)) {
			setError('End date must be after start date')
			return
		}

		const daysDiff = endDate.diff(startDate, 'day') + 1

		if (daysDiff > 365) {
			setError('Maximum 1 year (365 days) allowed')
			return
		}

		setError('')
		onSave(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'))
		onClose()
	}

	const handleStartDateChange = (newValue: Dayjs | null) => {
		setStartDate(newValue)
		setError('')
	}

	const handleStartDateAccept = (newValue: Dayjs | null) => {
		setStartDate(newValue)
		setError('')

		// If start date is set and end date exists, and start date > end date, reset end date
		if (newValue && endDate && newValue.isAfter(endDate)) {
			setEndDate(null)
		}
	}

	const handleEndDateChange = (newValue: Dayjs | null) => {
		setEndDate(newValue)
		setError('')
	}

	const handleEndDateAccept = (newValue: Dayjs | null) => {
		setEndDate(newValue)
		setError('')

		// If end date is set and start date exists, and end date < start date, reset start date
		if (newValue && startDate && newValue.isBefore(startDate)) {
			setStartDate(null)
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Edit Trip Dates"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			footer={
				<ButtonGroup>
					<CancelButton onClick={onClose} disabled={isUpdating}>
						Cancel
					</CancelButton>
					<SaveButton onClick={handleSave} disabled={!startDate || !endDate || isUpdating}>
						{isUpdating ? 'Saving...' : 'Save Changes'}
					</SaveButton>
				</ButtonGroup>
			}
		>
			<ModalContent>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DateInputGroup>
						<DateLabel>Start Date</DateLabel>
						<DateInput>
							<DatePicker
								value={startDate}
								onChange={handleStartDateChange}
								onAccept={handleStartDateAccept}
								referenceDate={endDate || undefined}
								minDate={dayjs().subtract(2, 'year')}
								maxDate={dayjs().add(10, 'year')}
								format="MM/DD/YYYY"
								slotProps={{
									textField: {
										placeholder: 'Select start date',
										fullWidth: true,
									},
								}}
							/>
						</DateInput>
					</DateInputGroup>

					<DateInputGroup>
						<DateLabel>End Date</DateLabel>
						<DateInput>
							<DatePicker
								value={endDate}
								onChange={handleEndDateChange}
								onAccept={handleEndDateAccept}
								referenceDate={startDate || undefined}
								minDate={dayjs().subtract(2, 'year')}
								maxDate={dayjs().add(10, 'year')}
								format="MM/DD/YYYY"
								slotProps={{
									textField: {
										placeholder: 'Select end date',
										fullWidth: true,
									},
								}}
							/>
						</DateInput>
					</DateInputGroup>
				</LocalizationProvider>

				{error && <ErrorMessage>{error}</ErrorMessage>}

				{!error && startDate && endDate && (
					<UnsavedChangesText>
						Trip duration: {endDate.diff(startDate, 'day') + 1} days
					</UnsavedChangesText>
				)}

			</ModalContent>
		</Modal>
	)
}

export default EditTripDatesModal

